import useSWR, { mutate } from "swr"
import API from "./api"
import { isObjectWithProperty } from "web/services/helpers"

export default function useCollection({
  key,
  parentKeys = [],
  updatePath,
  url,
  params = {},
  swrOptions = {},
  resourceType = undefined,
  withMutateFns = true,
}) {
  const apiPath = url || key

  function getWithParams() {
    return API.get(apiPath, params)
  }

  const {
    data: response,
    mutate: boundMutate,
    isValidating,
    isLoading,
  } = useSWR(key, getWithParams, {
    suspense: true,
    ...swrOptions,
  })

  async function add(data) {
    const newEl = await API.post(apiPath, data)

    await mutate(key)
    parentKeys.map((key) => mutate(key))

    if (typeof newEl === "object") {
      newEl.update = update(newEl.id)
      newEl.destroy = destroy(newEl.id)
    }

    return newEl
  }

  function localUpdateElement(id, data) {
    const updatedData = { ...response }
    const elementIndex = updatedData.detail.findIndex((el) => el.id === id)

    updatedData.detail[elementIndex] = data

    mutate(key, updatedData, false)
  }

  function update(id) {
    return async (data, localData) => {
      if (localData) {
        localUpdateElement(id, localData)
      }

      await API.put(`${updatePath || apiPath}/${id}`, data)
      return Promise.all([mutate(key), ...parentKeys.map((key) => mutate(key))])
    }
  }

  function destroy(id) {
    return async () => {
      const response = await API.delete(`${updatePath || apiPath}/${id}`)
      await Promise.all([mutate(key), ...parentKeys.map((key) => mutate(key))])

      return response
    }
  }

  async function destroyMany(itemsToDestroy) {
    const deletePromises = []
    for (const item of itemsToDestroy) {
      deletePromises.push(API.delete(`${updatePath || apiPath}/${item.id}`))
    }

    await Promise.all(deletePromises)

    return Promise.all([mutate(key), ...parentKeys.map((key) => mutate(key))])
  }

  const hasSummaryAndDetail = isObjectWithProperty(response, "detail")
  const data = hasSummaryAndDetail ? response.detail : response || []

  data.add = add
  data.destroy = destroyMany
  data.refresh = () => mutate(key)
  data.mutate = boundMutate
  data.isValidating = isValidating
  data.isLoading = isLoading

  if (withMutateFns) {
    for (const el of data) {
      el.update = update(el.id)
      el.destroy = destroy(el.id)
      el.refresh = () => mutate(key)
      if (resourceType) el.resource_type = resourceType
    }
  }

  return hasSummaryAndDetail ? { detail: data, ...response } : data
}
